import { useCallback, useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Content,
  Header,
  Link,
  Page,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { Chip, Tooltip, IconButton, makeStyles } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SyncIcon from '@material-ui/icons/Sync';

const useStyles = makeStyles(theme => ({
  chip: {
    height: 22,
    fontSize: '0.75rem',
  },
  running: { backgroundColor: '#228722', color: '#fff' },
  provisioning: { backgroundColor: '#ff7900', color: '#fff' },
  deleting: { backgroundColor: '#cd3c14', color: '#fff' },
  unknown: { backgroundColor: theme.palette.grey[500], color: '#fff' },
}));

type ClusterRow = {
  name: string;
  title: string;
  phase: string;
  k8sVersion: string;
  region: string;
  clusterType: string;
  apiServerUrl: string;
  entityRef: string;
};

function phaseClass(
  phase: string,
  classes: ReturnType<typeof useStyles>,
): string {
  const p = phase.toLowerCase();
  if (p === 'running') return classes.running;
  if (p === 'provisioning' || p === 'scaling') return classes.provisioning;
  if (p === 'deleting' || p === 'failed') return classes.deleting;
  return classes.unknown;
}

function toRow(entity: Entity): ClusterRow {
  const ann = entity.metadata.annotations ?? {};
  const ns = entity.metadata.namespace ?? 'default';
  return {
    name: entity.metadata.name,
    title: entity.metadata.title ?? entity.metadata.name,
    phase: ann['mks.io/phase'] ?? 'Unknown',
    k8sVersion: ann['mks.io/kubernetes-version'] ?? '—',
    region: ann['mks.io/region'] ?? '—',
    clusterType: ann['mks.io/cluster-type'] ?? '—',
    apiServerUrl: ann['mks.io/api-server-url'] ?? '',
    entityRef: `resource:${ns}/${entity.metadata.name}`,
  };
}

export const ClustersPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [rows, setRows] = useState<ClusterRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const response = await catalogApi.getEntities({
      filter: { kind: 'Resource', 'spec.type': 'kubernetes-cluster' },
      fields: ['kind', 'metadata', 'spec'],
    });
    setRows(response.items.map(toRow));
    setLoading(false);
  }, [catalogApi]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: TableColumn<ClusterRow>[] = [
    {
      title: 'Name',
      field: 'title',
      render: row => (
        <Link to={`/catalog/${row.entityRef.split(':')[1].split('/')[0]}/resource/${row.name}`} style={{ fontWeight: 500 }}>
          {row.title}
        </Link>
      ),
    },
    {
      title: 'Phase',
      field: 'phase',
      render: row => (
        <Chip
          label={row.phase}
          className={`${classes.chip} ${phaseClass(row.phase, classes)}`}
        />
      ),
    },
    { title: 'K8s Version', field: 'k8sVersion' },
    { title: 'Region', field: 'region' },
    { title: 'Type', field: 'clusterType' },
    {
      title: 'API Server',
      field: 'apiServerUrl',
      render: row =>
        row.apiServerUrl ? (
          <Tooltip title={row.apiServerUrl}>
            <a
              href={row.apiServerUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            >
              {row.apiServerUrl.replace(/^https?:\/\//, '')}
            </a>
          </Tooltip>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        ),
    },
    {
      title: '',
      field: 'entityRef',
      width: '48px',
      render: row => (
        <Tooltip title="Open entity page">
          <Link to={`/catalog/${row.entityRef.split(':')[1].split('/')[0]}/resource/${row.name}`}>
            <IconButton size="small">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Link>
        </Tooltip>
      ),
    },
  ];

  return (
    <Page themeId="tool">
      <Header title="Clusters" subtitle="MKS workload clusters" />
      <Content>
        <Table<ClusterRow>
          title={`Clusters (${rows.length})`}
          isLoading={loading}
          data={rows}
          columns={columns}
          options={{
            paging: false,
            search: true,
            columnsButton: true,
          }}
          actions={[
            {
              icon: () => <SyncIcon />,
              tooltip: 'Refresh',
              isFreeAction: true,
              onClick: load,
            },
          ]}
        />
      </Content>
    </Page>
  );
};
